/**
 * Domain Security Layer - First line of defense against unauthorized access
 * Implements fail-secure domain validation to protect against accidental access
 * and warn potential attackers.
 */

export class DomainSecurity {
  static ADMIN_DOMAINS = [
    'admin.orgadoro.com',
    'localhost:3000',
    '127.0.0.1:3000'
  ];
  
  static USER_DOMAINS = ['app.orgadoro.com'];
  
  // Environment-specific redirect URLs
  static getUserAppUrl() {
    // Use environment variable if set, otherwise fall back to defaults
    const envRedirectUrl = process.env.REACT_APP_USER_APP_URL;
    
    if (envRedirectUrl) {
      return envRedirectUrl;
    }
    
    // Fallback logic for environments without the env var
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:3001'; // Development fallback
    }
    
    return 'https://app.orgadoro.com'; // Production fallback
  }
  
  static validateDomain() {
    try {
      const currentHost = window.location.host;
      
      // Fail secure: if we can't determine the host, deny access
      if (!currentHost) {
        this.handleInvalidDomain('unknown_host');
        return false;
      }
      
      const isValidAdmin = this.ADMIN_DOMAINS.includes(currentHost);
      
      if (!isValidAdmin) {
        this.handleInvalidDomain(currentHost);
        return false;
      }
      
      return true;
    } catch (error) {
      // Fail secure: any error in validation denies access
      this.handleInvalidDomain('validation_error');
      return false;
    }
  }
  
  static handleInvalidDomain(attemptedHost) {
    this.logUnauthorizedAccess(attemptedHost);
    this.showUnauthorizedWarning();
    this.redirectToUserSite();
  }
  
  static showUnauthorizedWarning() {
    document.body.innerHTML = `
      <div style="display: flex; justify-content: center; align-items: center; 
                  min-height: 100vh; background: #fef2f2; font-family: system-ui;">
        <div style="max-width: 500px; padding: 2rem; background: white; 
                    border: 2px solid #dc2626; border-radius: 8px; text-align: center;">
          <h1 style="color: #dc2626; margin-bottom: 1rem;">
            ⚠️ UNAUTHORIZED ACCESS ATTEMPT
          </h1>
          <p><strong>This is a restricted administrative portal.</strong></p>
          <p>Access is limited to authorized administrators only.</p>
          <div style="background: #fef2f2; border: 1px solid #fecaca; 
                      border-radius: 4px; padding: 1rem; margin: 1rem 0;">
            <p style="color: #7f1d1d; margin: 0;">
              <strong>⚠️ WARNING:</strong> Unauthorized access attempts are logged 
              and monitored. Repeated attempts may result in account suspension 
              and legal action.
            </p>
          </div>
          <p>If you're looking for the Orgadoro app, you'll be redirected in 5 seconds...</p>
          <div style="margin-top: 2rem;">
            <a href="${this.getUserAppUrl()}" 
               style="background: #3b82f6; color: white; padding: 0.75rem 1.5rem; 
                      text-decoration: none; border-radius: 4px; display: inline-block;">
              Go to Orgadoro App →
            </a>
          </div>
        </div>
      </div>
    `;
  }
  
  static redirectToUserSite() {
    setTimeout(() => {
      window.location.href = this.getUserAppUrl();
    }, 5000);
  }
  
  static logUnauthorizedAccess(attemptedHost) {
    // Send to security monitoring system
    // Fail secure: if logging fails, don't prevent the redirect
    try {
      const logData = {
        timestamp: new Date().toISOString(),
        attemptedHost,
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        eventType: 'unauthorized_domain_access',
        severity: 'warning'
      };
      
      // For now, log to console - will be enhanced with actual logging system
      console.warn('🚨 Security Event: Unauthorized domain access attempt', logData);
      
      // Future: Send to audit logging system
      // fetch('/api/security/log-unauthorized-access', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(logData)
      // }).catch(() => {}); // Fail silently
    } catch (error) {
      // Fail silently - logging failure shouldn't prevent security action
    }
  }
}
